import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { MessageCircle, Trash2, CheckCircle2, UserRound } from "lucide-react";

const OUTCOME = {
  resolved: { label: "Esclarecida", color: "#22C55E", icon: CheckCircle2 },
  escalated: { label: "Passou ao secretário", color: "#E50000", icon: UserRound },
  abandoned: { label: "Deixada a meio", color: "#737373", icon: MessageCircle },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

/* Arquivo das conversas do Mike — só o admin vê. Serve para saber o que não está claro. */
export default function MikeChats() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = () => api.get(`/assistant/chats${filter ? `?outcome=${filter}` : ""}`)
    .then((r) => setRows(r.data || []))
    .catch((err) => toast.error(formatApiError(err)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const remove = async (id) => {
    try { await api.delete(`/assistant/chats/${id}`); toast.success("Conversa apagada"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!rows) return <div className="kw-overline">A carregar…</div>;

  const counts = rows.reduce((a, r) => ({ ...a, [r.outcome]: (a[r.outcome] || 0) + 1 }), {});

  return (
    <div className="space-y-6 kw-fade-up" data-testid="mike-chats-page">
      <div className="flex items-center gap-4">
        <img src="/mike.jpg" alt="Mike" className="w-14 h-14 rounded-full object-cover border border-[#E50000]" />
        <div>
          <div className="kw-overline">Arquivo privado · só admin</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter">
            Conversas do <span className="text-[#E50000]">Mike</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-1 max-w-2xl">
            Cada conversa fica arquivada quando é esclarecida ou quando passa para um coach.
            Lê-as para saber o que ainda não está claro na Liga.
          </p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <button onClick={() => setFilter("")}
          className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${!filter ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3]"}`}
          data-testid="mike-filter-all">Todas · {rows.length}</button>
        {Object.entries(OUTCOME).map(([k, o]) => (
          <button key={k} onClick={() => setFilter(k)}
            className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border"
            style={filter === k ? { background: o.color, borderColor: o.color, color: "#fff" } : { borderColor: o.color, color: o.color }}
            data-testid={`mike-filter-${k}`}>
            {o.label}{counts[k] ? ` · ${counts[k]}` : ""}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="kw-card p-10 text-center text-[#737373] text-sm" data-testid="mike-chats-empty">
          Ainda não há conversas arquivadas. Aparecem aqui quando alguém fecha uma conversa com o Mike
          ou pede para falar com o coach.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const o = OUTCOME[r.outcome] || OUTCOME.abandoned;
            const isOpen = openId === r.id;
            return (
              <div key={r.id} className="kw-card p-0" data-testid={`mike-chat-${r.id}`}>
                <div className="p-4 flex items-start gap-3">
                  <o.icon className="w-4 h-4 mt-1 shrink-0" style={{ color: o.color }} />
                  <button onClick={() => setOpenId(isOpen ? null : r.id)} className="flex-1 text-left min-w-0">
                    <div className="text-sm font-bold truncate">{r.question_summary || "(sem pergunta registada)"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                      {r.user_name}{r.club ? ` · ${r.club}` : ""} · {r.turns} mensagens · {fmt(r.created_at)}
                      {r.escalated_to && <span className="text-[#E50000]"> · avisado {r.escalated_to}</span>}
                    </div>
                  </button>
                  <span className="text-[10px] uppercase tracking-widest shrink-0" style={{ color: o.color }}>{o.label}</span>
                  <button onClick={() => remove(r.id)} className="text-[#555] hover:text-[#E50000] p-1 shrink-0"
                    title="Apagar conversa" data-testid={`mike-chat-del-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {isOpen && (
                  <div className="px-4 pb-4 space-y-2 border-t border-[#161616] pt-3" data-testid={`mike-chat-body-${r.id}`}>
                    {(r.transcript || []).map((t, i) => (
                      <div key={i} className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] px-3 py-2 text-sm ${t.role === "user" ? "bg-[#E50000]/15 border border-[#E50000]/40" : "bg-[#141414] border border-[#222]"}`}>
                          <div className="text-[9px] uppercase tracking-widest text-[#737373] mb-1">
                            {t.role === "user" ? r.user_name : "Mike"}
                          </div>
                          {t.text}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
